"use client";

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { ArrowLeft, Newspaper, Search, RefreshCw } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { useNewsStore } from "@/vybe-trader-client/news/stores/useNewsStore";
import type { NewsArticle } from "@/vybe-trader-client/news/stores/useNewsStore";
import {
  type NewsFilterType,
  filterNewsArticles,
  formatPublishedAt,
  ImportanceMark,
  listHeadline,
} from "@/vybe-trader-client/news/newsView";
import { prepareAndSyncNews } from "@/vybe-trader-client/news/syncNews";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { DENSITY } from "@/lib/density-scale";
import NewsFilterChips from "./NewsFilterChips";
import NewsReader from "./NewsReader";

/** Pixels of over-scroll pull needed before a release triggers a refresh. */
const PTR_THRESHOLD = 64;

interface NewsScreenProps {
  onBack: () => void;
}

/**
 * Mobile News screen.
 *
 * Rendered as a Settings sub-screen (same idiom as Economic Calendar): it fills
 * the shell's content slot, so the bottom navigation stays mounted underneath
 * and the header carries a back arrow instead of a close button. Feed → reader
 * is a push-in inside the same slot. Pure view layer: it reuses `useNewsStore`,
 * the `newsView` helpers and the existing `syncNews` path unchanged.
 */
export default function NewsScreen({ onBack }: NewsScreenProps) {
  const articles = useNewsStore((s) => s.articles);
  const markRead = useNewsStore((s) => s.markRead);
  const isRead = useNewsStore((s) => s.isRead);
  const { client } = useVybeTraderClient();

  const [filterType, setFilterType] = useState<NewsFilterType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [readingIndex, setReadingIndex] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pullY, setPullY] = useState(0);

  const filtered = useMemo(
    () => filterNewsArticles(articles, filterType, searchQuery),
    [articles, filterType, searchQuery]
  );

  const unreadCount = useMemo(
    () => articles.filter((a) => !isRead(a.id)).length,
    [articles, isRead]
  );

  // ── Read-state marks as the user opens / pages a story ──
  const openReader = useCallback(
    (index: number) => {
      const article = filtered[index];
      if (article) markRead(article.id);
      setReadingIndex(index);
    },
    [filtered, markRead]
  );

  const navigateReader = useCallback(
    (index: number) => {
      const article = filtered[index];
      if (article) markRead(article.id);
      setReadingIndex(index);
    },
    [filtered, markRead]
  );

  // Clamp / drop a stale reading index if the filtered list shrinks under it
  // (e.g. the filter changed or a refresh removed rows).
  useEffect(() => {
    if (readingIndex !== null && readingIndex >= filtered.length) {
      setReadingIndex(filtered.length > 0 ? filtered.length - 1 : null);
    }
  }, [filtered.length, readingIndex]);

  // ── Refresh (shared by the button and pull-to-refresh) ──
  const runRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await prepareAndSyncNews(client);
    } catch {
      /* best-effort; the feed keeps showing the cached snapshot */
    } finally {
      setRefreshing(false);
      setPullY(0);
    }
  }, [client, refreshing]);

  // Kick a sync when the screen mounts (same path the connect flow already runs).
  useEffect(() => {
    void runRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Pull-to-refresh gesture on the feed scroller ──
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pullStartRef = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current;
    if (el && el.scrollTop <= 0) pullStartRef.current = e.touches[0].clientY;
    else pullStartRef.current = null;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (pullStartRef.current === null) return;
    const dy = e.touches[0].clientY - pullStartRef.current;
    if (dy > 0) {
      // Dampen the pull so it feels rubber-banded.
      setPullY(Math.min(dy * 0.5, PTR_THRESHOLD * 1.5));
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (pullStartRef.current !== null && pullY >= PTR_THRESHOLD) {
      void runRefresh();
    } else {
      setPullY(0);
    }
    pullStartRef.current = null;
  }, [pullY, runRefresh]);

  // Reader is a push-in over the same slot — the bottom nav stays put.
  if (readingIndex !== null && filtered[readingIndex]) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
        <NewsReader
          list={filtered}
          index={readingIndex}
          onNavigate={navigateReader}
          onClose={() => setReadingIndex(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground text-xs">
      {/* Header — back · title · refresh (Economic Calendar idiom) */}
      <div className="flex h-10 shrink-0 items-center border-b border-border px-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onBack}
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4 text-primary" />
        </Button>
        <span className="flex-1 text-center font-medium text-muted-foreground">
          News
          {unreadCount > 0 ? (
            <span className="ml-1.5 font-mono text-[9.5px] text-orange-500">
              {unreadCount} unread
            </span>
          ) : null}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={runRefresh}
          className={`${DENSITY.ICON_BTN} shrink-0`}
          aria-label="Refresh news"
        >
          <RefreshCw
            className={`h-4 w-4 text-muted-foreground ${refreshing ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      {/* Search */}
      <div className="shrink-0 px-3 pt-2 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search articles…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-9 text-sm"
          />
        </div>
      </div>

      {/* Sticky filter chips */}
      <NewsFilterChips value={filterType} onChange={setFilterType} />

      {/* Momentum feed with pull-to-refresh */}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        <div
          className="flex items-center justify-center overflow-hidden font-mono text-[9px] text-muted-foreground transition-[height]"
          style={{ height: refreshing ? 26 : pullY }}
        >
          {refreshing ? (
            <span className="flex items-center gap-1.5">
              <RefreshCw className="h-3 w-3 animate-spin" /> Refreshing…
            </span>
          ) : pullY >= PTR_THRESHOLD ? (
            <span className="text-green-500">↻ Release to refresh</span>
          ) : pullY > 0 ? (
            <span>↻ Pull to refresh</span>
          ) : null}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
            <Newspaper className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No articles yet</p>
          </div>
        ) : (
          filtered.map((article: NewsArticle, i) => {
            const unread = !isRead(article.id);
            const { time } = formatPublishedAt(article.publishedAtUnix);
            const hasThumb = Boolean(article.imageUrl);
            return (
              <button
                key={article.id}
                type="button"
                onClick={() => openReader(i)}
                className={`flex w-full items-start gap-2.5 border-b border-border px-3 py-2.5 text-left active:bg-muted/40 ${
                  unread ? "bg-blue-400/5" : ""
                }`}
              >
                {/* Thumbnail — image, else a token gradient tile */}
                {hasThumb ? (
                  <img
                    src={article.imageUrl}
                    alt=""
                    className="h-11 w-11 shrink-0 rounded-md border border-border object-cover"
                  />
                ) : (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border bg-gradient-to-br from-muted to-muted/40">
                    <Newspaper className="h-4 w-4 text-muted-foreground/50" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-1.5">
                    {unread && (
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                    )}
                    <span
                      className={`line-clamp-2 text-[12px] leading-snug ${
                        unread ? "font-semibold text-foreground" : "font-medium text-foreground/70"
                      }`}
                    >
                      {listHeadline(article)}
                    </span>
                  </div>
                  {(article.summary || article.body) && (
                    <p className="mt-0.5 line-clamp-2 text-[10.5px] leading-snug text-muted-foreground">
                      {article.summary || article.body.slice(0, 140)}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-1.5 font-mono text-[9px] text-muted-foreground">
                    <ImportanceMark
                      importance={article.importance}
                      isBreaking={article.isBreaking}
                    />
                    {article.category && (
                      <span className="rounded bg-muted px-1 py-px text-[8.5px] text-muted-foreground">
                        {article.category}
                      </span>
                    )}
                    <span>· {time}</span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
